import * as React from 'react'
import { useState } from 'react'

import { Box, DoubleRangeSlider } from '@factorialco/deprecated-design-system'
import { isUndefined } from 'lodash'

import Chip from 'modules/core/components/Filter/base/Chip'
import ClearValue from 'modules/core/components/Filter/base/ClearValue'
import Content from 'modules/core/components/Filter/base/Content'
import Provider from 'modules/core/components/Filter/base/Provider'
import useFilter from 'modules/core/components/Filter/base/useFilter'

type Range = [number, number]

type Props<T> = {
  label: string
  value?: T
  onValueChange: (value?: T) => void
  defaultOpen?: boolean
  range: Range
}

type BodyProps = {
  range: Range
}

const DEBOUNCE_TIME_MS = 700

function Body({ range }: BodyProps) {
  const [low, top] = range
  const { value, onValueChange } = useFilter<Range>()
  const [internalRange, setInternalRange] = useState<Range>([value?.[0] || low, value?.[1] || top])
  const timeoutId = React.useRef<ReturnType<typeof setTimeout>>()

  React.useEffect(() => {
    timeoutId.current = setTimeout(() => onValueChange(internalRange), DEBOUNCE_TIME_MS)
  }, [internalRange])

  return (
    <Box
      background="lighter000"
      flexDirection="column"
      padding="s16"
      width="s300"
      height="s80"
      gap="s8"
    >
      <DoubleRangeSlider
        min={low}
        max={top}
        value={internalRange}
        onChange={([min, max]) => {
          if (timeoutId.current) {
            clearTimeout(timeoutId.current)
          }

          setInternalRange([Math.round(min), Math.round(max)])
        }}
      />
    </Box>
  )
}

function RangePicker<T>({ label, value, onValueChange, defaultOpen, range }: Props<T>) {
  return (
    <Provider
      closeOnChange={false}
      value={value}
      onValueChange={onValueChange}
      defaultOpen={!!defaultOpen}
    >
      <Chip.Toggler active={!isUndefined(value)} label={label} />
      <Content body={<Body range={range} />} footer={<ClearValue />} />
    </Provider>
  )
}

export default RangePicker
